import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";

/**
 * Google-Calendar-style "this event / all future events" picker.
 * Shown when a host acts on a session that belongs to a series.
 *
 * Props:
 *  - open, onOpenChange — controlled visibility
 *  - title, description — copy
 *  - actionLabel — "Cancel" | "Delete" | "Edit"
 *  - onConfirm(scope) — called with "single" or "all_future"
 *  - destructive — uses destructive styling on the confirm button
 */
const SeriesScopeDialog = ({
  open,
  onOpenChange,
  title = "This session is part of a series",
  description = "Pick what you want to apply this change to.",
  actionLabel = "Confirm",
  onConfirm,
  destructive = false,
}) => {
  const [scope, setScope] = useState("single");

  const handleConfirm = () => {
    onOpenChange(false);
    onConfirm(scope);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="series-scope-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {[
            { value: "single", label: "This session only", desc: "Only this one date is affected. Other sessions in the series stay as they are." },
            { value: "all_future", label: "This session and all future sessions", desc: "Applies to this date plus every later session in the series. Past sessions are untouched." },
          ].map(opt => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                scope === opt.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
              }`}
              data-testid={`scope-${opt.value}`}
            >
              <input
                type="radio"
                name="series-scope"
                value={opt.value}
                checked={scope === opt.value}
                onChange={() => setScope(opt.value)}
                className="mt-1 accent-primary"
              />
              <div className="flex-1">
                <p className="font-medium leading-tight">{opt.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            className={destructive
              ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              : "bg-primary text-primary-foreground hover:bg-primary/90"}
            data-testid="series-scope-confirm"
          >
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SeriesScopeDialog;
